import React, { useCallback, useContext } from 'react';
import { FormSpy } from 'react-final-form';
import { FormState } from 'final-form';
import { Listing } from 'models/Listing';
import { AiDraftedFieldEditedEventProps } from 'models/MixpanelEvents';
import StepsContext from 'providers/Steps/Steps.context';
import mixpanelService from 'services/mixpanelService';
import sessionStorageService from 'services/sessionStorageService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getHasElementsInArray } from 'utils';
import useAiDraftedListing from '../../hooks/useAiDraftedListing';
import {
  AI_DRAFTED_FIELD_STEPS,
  getReportedFieldsStorageKey,
  getRootFieldName,
  ReportedFields,
} from './utils';

/**
 * Reports a broker's edit to an AI-drafted field, once per field per session -
 * following the value or the blur instead would turn one field the broker moves in
 * and out of into a dozen events. Renders nothing, so mount it anywhere inside the
 * wizard form.
 */
const AiDraftedFieldsTracker: React.FC = () => {
  const { currentStep } = useContext(StepsContext);

  const {
    listingId,
    dealRoomId,
    dealName,
    listingName,
    isAiDrafted,
    imExtractedFields,
  } = useAiDraftedListing();

  const getMixpanelProps = useGetMixpanelProps();

  const step = AI_DRAFTED_FIELD_STEPS[currentStep];

  // A field counts as edited once its value differs from what AI drafted, which is
  // exactly what final-form calls dirty - the wizard initialises the form from the
  // drafted listing.
  const onChange = useCallback(
    ({ dirtyFields }: FormState<Partial<Listing>>) => {
      const storageKey = getReportedFieldsStorageKey(listingId);

      const reportedFields =
        sessionStorageService.getItem<ReportedFields>(storageKey) || {};

      const editedFields = Object.keys(dirtyFields || {})
        .map(getRootFieldName)
        .filter(
          (fieldName, index, fieldNames) =>
            fieldNames.indexOf(fieldName) === index &&
            imExtractedFields.includes(fieldName) &&
            !reportedFields[fieldName],
        );

      if (!getHasElementsInArray(editedFields)) return;

      sessionStorageService.setItem(
        storageKey,
        editedFields.reduce(
          (acc, fieldName) => ({ ...acc, [fieldName]: true }),
          reportedFields,
        ),
      );

      editedFields.forEach((fieldName) =>
        mixpanelService.aiDraftedFieldEdited(
          getMixpanelProps({
            dealId: dealRoomId,
            dealName,
            fieldName,
            step,
            listingName,
          }) as AiDraftedFieldEditedEventProps,
        ),
      );
    },
    [
      dealName,
      dealRoomId,
      getMixpanelProps,
      imExtractedFields,
      listingId,
      listingName,
      step,
    ],
  );

  if (!isAiDrafted || !step || !getHasElementsInArray(imExtractedFields))
    return null;

  return <FormSpy subscription={{ dirtyFields: true }} onChange={onChange} />;
};

export default AiDraftedFieldsTracker;
